import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Trash2,
  Users,
  Dices,
  Network,
  List,
  Trophy,
  Settings,
} from 'lucide-react';
import { useTournament } from '@/data/use-tournament';
import { deleteTournament } from '@/data/tournaments';
import { ParticipantsTab } from '@/features/participants/participants-tab';
import { SeedingTab } from '@/features/seeding/seeding-tab';
import { BracketTab } from '@/features/tournament-dashboard/bracket-tab';
import { MatchesTab } from '@/features/tournament-dashboard/matches-tab';
import { ResultsTab } from '@/features/tournament-dashboard/results-tab';
import { TournamentSettingsTab } from '@/features/tournament-dashboard/tournament-settings-tab';
import { ShareCard } from '@/features/share/share-card';
import { toast } from '@/components/ui/sonner';
import { AppHeader, StatusBadge, LoadingState, ErrorState, EmptyState } from '@/components/design';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const FORMAT_LABELS: Record<string, string> = {
  SINGLE_ELIM: 'Олимпийка',
  DOUBLE_ELIM: 'Двойная олимпийка',
  ROUND_ROBIN: 'Круговая',
  SWISS: 'Швейцарка',
  GROUPS_PLAYOFF: 'Группы + плей-офф',
};

const TABS = [
  { id: 'participants', label: 'Участники', icon: Users },
  { id: 'seeding', label: 'Жеребьёвка', icon: Dices },
  { id: 'bracket', label: 'Сетка', icon: Network },
  { id: 'matches', label: 'Матчи', icon: List },
  { id: 'results', label: 'Результаты', icon: Trophy },
  { id: 'settings', label: 'Настройки', icon: Settings },
] as const;

export function TournamentDetail() {
  const { id } = useParams<{ id: string }>();
  const { tournament, error } = useTournament(id);
  const navigate = useNavigate();
  const [tab, setTab] = useState<string>('participants');
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Стартовая вкладка зависит от статуса турнира — выставляем один раз,
  // когда турнир загрузился, дальше пользователь переключает сам.
  const initialTabApplied = useRef(false);
  useEffect(() => {
    if (initialTabApplied.current || !tournament) return;
    initialTabApplied.current = true;
    if (tournament.status === 'IN_PROGRESS') setTab('bracket');
    else if (tournament.status === 'COMPLETED') setTab('results');
  }, [tournament]);

  async function handleDelete() {
    if (!id) return;
    try {
      await deleteTournament(id);
      toast.success('Турнир удалён');
      navigate('/tournaments');
    } catch (err) {
      toast.error('Не получилось удалить', { description: String((err as Error).message) });
    }
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <AppHeader showUser />
        <div className="container-d" style={{ paddingTop: 20, paddingBottom: 64 }}>
          <ErrorState title="Ошибка" body={error.message} />
        </div>
      </div>
    );
  }

  if (tournament === undefined) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <AppHeader showUser />
        <div className="container-d" style={{ paddingTop: 20, paddingBottom: 64 }}>
          <LoadingState />
        </div>
      </div>
    );
  }

  if (tournament === null) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <AppHeader showUser />
        <div className="container-d" style={{ paddingTop: 20, paddingBottom: 64 }}>
          <EmptyState
            icon={Trophy}
            title="Турнир не найден"
            body="Возможно, он был удалён или ссылка неверна."
          />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      <AppHeader showUser />
      <div className="container-d" style={{ paddingTop: 20, paddingBottom: 64 }}>
        <Link
          to="/tournaments"
          className="btn btn-ghost btn-sm"
          style={{ marginBottom: 12, paddingLeft: 8 }}
        >
          <ArrowLeft size={14} />К турнирам
        </Link>

        <div className="row gap-4" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div className="row gap-3">
              <h1 className="td-h1">{tournament.name}</h1>
              <StatusBadge status={tournament.status} />
            </div>
            <div className="row gap-2" style={{ marginTop: 6, color: 'var(--fg-muted)' }}>
              <Calendar size={14} />
              <span className="t-small">
                {tournament.date.toDate().toLocaleDateString('ru-RU')}
              </span>
              <span style={{ color: 'var(--fg-faint)' }}>·</span>
              <span className="t-small">{FORMAT_LABELS[tournament.format] ?? tournament.format}</span>
              <span style={{ color: 'var(--fg-faint)' }}>·</span>
              <span className="t-small">
                {tournament.discipline === 'SINGLES' ? 'Одиночка' : 'Пары'}
              </span>
              <span style={{ color: 'var(--fg-faint)' }}>·</span>
              <span className="t-small">{tournament.matchFormat}</span>
              <span style={{ color: 'var(--fg-faint)' }}>·</span>
              <span className="t-small">{tournament.participantCount} участников</span>
            </div>
          </div>
          <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(true)}>
            <Trash2 size={14} />
            Удалить турнир
          </button>
        </div>

        <div style={{ marginTop: 4, marginBottom: 0 }}>
          <ShareCard slug={tournament.slug} />
        </div>

        <div className="tabs" style={{ marginTop: 24 }}>
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                className="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
              >
                <span className="row gap-2">
                  <Icon size={14} />
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>

        <div style={{ paddingTop: 24 }}>
          {tab === 'participants' && <ParticipantsTab tournament={tournament} />}
          {tab === 'seeding' && <SeedingTab tournament={tournament} />}
          {tab === 'bracket' && <BracketTab tournament={tournament} />}
          {tab === 'matches' && <MatchesTab tournament={tournament} />}
          {tab === 'results' && <ResultsTab tournament={tournament} />}
          {tab === 'settings' && <TournamentSettingsTab tournament={tournament} />}
        </div>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Удалить турнир?</DialogTitle>
            <DialogDescription>
              Это действие нельзя отменить. Все участники, матчи и результаты будут удалены.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button className="btn" onClick={() => setConfirmDelete(false)}>
              Отмена
            </button>
            <button
              className="btn btn-danger"
              onClick={async () => {
                setConfirmDelete(false);
                await handleDelete();
              }}
            >
              <Trash2 size={14} />
              Удалить
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
